// FTEDN Team - Creator Management Page
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface Creator {
  id: string;
  displayName?: string;
  name?: string;
  email?: string;
  status?: string;
  role?: string;
  createdAt?: string;
}

const STATUS_REASON_MIN_LENGTH = 5;
const STATUS_REASON_ERROR = 'A reason (5+ chars) is required';

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

export function CreatorsPage() {
  const { get, patch } = useApi();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [reasonDrafts, setReasonDrafts] = useState<Record<string, string>>({});
  const [reasonErrors, setReasonErrors] = useState<Record<string, string>>({});

  const loadCreators = useCallback(async () => {
    setError('');
    try {
      setCreators(getItems<Creator>(await get('/creators')));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load creators');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadCreators();
  }, [loadCreators]);

  async function updateCreatorStatus(creator: Creator, status: 'active' | 'suspended') {
    const statusChangeReason = (reasonDrafts[creator.id] ?? '').trim();
    if (statusChangeReason.length < STATUS_REASON_MIN_LENGTH) {
      setReasonErrors((current) => ({ ...current, [creator.id]: STATUS_REASON_ERROR }));
      return;
    }

    setActingId(creator.id);
    setError('');
    setReasonErrors((current) => ({ ...current, [creator.id]: '' }));
    try {
      await patch(`/creators/${creator.id}`, { status, statusChangeReason });
      setReasonDrafts((current) => ({ ...current, [creator.id]: '' }));
      await loadCreators();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update creator');
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Creators</h1>
        <p className="mgmt-subtitle">Manage creator account access and status</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{creators.length} Creators</span>
          <span className="stat-chip approved">{creators.filter((creator) => creator.status !== 'suspended').length} Active</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      <div className="mgmt-table">
        {loading && <div className="mgmt-empty">Loading creators...</div>}
        {!loading && creators.length === 0 && <div className="mgmt-empty">No creators found.</div>}
        {creators.map((creator) => {
          const displayName = creator.displayName || creator.name || creator.email || 'Unknown creator';
          const status = creator.status || 'active';
          const nextStatus = status === 'suspended' ? 'active' : 'suspended';
          const reasonInputId = `creator-status-reason-${creator.id}`;
          const reasonError = reasonErrors[creator.id];
          return (
            <div key={creator.id} className="mgmt-row">
              <div className="mgmt-row-info">
                <strong>{displayName}</strong>
                <span>{creator.email || 'No email'}</span>
                <span>{creator.role || 'creator'}</span>
              </div>
              <span className={`status-badge ${status}`}>{status}</span>
              <div className="mgmt-status-reason">
                <label className="mgmt-reason-label" htmlFor={reasonInputId}>Status reason</label>
                <input
                  id={reasonInputId}
                  className="mgmt-reason-input"
                  value={reasonDrafts[creator.id] ?? ''}
                  onChange={(event) => {
                    setReasonDrafts((current) => ({ ...current, [creator.id]: event.target.value }));
                    if (reasonError) {
                      setReasonErrors((current) => ({ ...current, [creator.id]: '' }));
                    }
                  }}
                  placeholder={`${nextStatus === 'active' ? 'Activation' : 'Suspension'} reason`}
                  aria-label={`Reason for ${displayName} status change`}
                  aria-invalid={reasonError ? 'true' : 'false'}
                  aria-describedby={reasonError ? `${reasonInputId}-error` : undefined}
                />
                {reasonError && (
                  <p id={`${reasonInputId}-error`} className="mgmt-action-error" role="alert">
                    {reasonError}
                  </p>
                )}
              </div>
              <div className="mgmt-actions">
                <button
                  className={`action-btn ${nextStatus === 'active' ? 'approve' : 'reject'}`}
                  disabled={actingId === creator.id}
                  onClick={() => void updateCreatorStatus(creator, nextStatus)}
                  aria-label={`${nextStatus === 'active' ? 'Activate' : 'Suspend'} ${displayName}`}
                >
                  {nextStatus === 'active' ? 'Activate' : 'Suspend'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
